// Angular
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
// Store
import { Selector, State, StateContext, Action } from '@ngxs/store';
import { ProductState } from '../products/state/product.state';
import { NexsysMonitorState } from '../nexsys-monitor/state/nexsys-monitor.state';
import { GenericIntegrationState } from '../generic-integrations/state/generic-integation.state';
// Actions
import { Logout } from '@cv-invision/core';
import {
	SetAppScopePropertyTypes,
	SetProductListByIntegration,
	SetAcuityLenderBranches,
	SetLenders,
	SetAppScopeLoanTypes,
	SetNexsysAttributeList,
	SetAcuityPropertyTypes,
	SetAcuityLoanTypes,
	SetAcuityLoanReasons,
	SetAcuityOccupancyTypes,
	SetAppScopeLoanReasons,
	SetAppScopeOccupancyTypes,
	SetNexsysMileStoneMessageStatus,
	SetAcuityDocumentTypes,
	SetAppScopeDocumentTypes,
	SetClassValuationDocumentTypes,
	SetFormTypes,
	SetOrigins,
	SetValutrustMileStoneMessageStatus,
	SetXpanseSettings,
	SetFNCSettings,
	SetGenericIntegrations,
	SetDeliveryAssetsAppScopeDocumentTypes,
	SetDeliveryAssetsAcuityDocumentTypes,
	SetDeliveryAssetsClassValuationDocumentTypes,
	SetCmpAcuityPropertyTypes,
	SetCmpAcuityLoanTypes,
	SetCmpAcuityOccupancyTypes,
	SetCmpAcuityLoanReasons,
	SetCmpAcuityDocumentTypes,
	SetDeliveryAssetsCmpAcuityDocumentTypes,
	SetCmpAcuityLenderBranches,
	SetAcuityMilestones,
	SetCmpAcuityMilestones,
	SetTrackingSystems,
	SetClassValuationDocTypes,
	SetActionOptions,
	SetCMPOnHoldReasons,
	SetReceiveFromClientOptions,
	SetHandleMode
} from './actions/global-configuration.actions';
// Models
import { INTEGRATIONS } from '../model/global-configuration.constant';
import {
	GenericIntegration,
	GlobalConfigurationStateModel,
	IdNameObjectType,
	ProductsList
} from './models/global-configuration-state.model';
import { DefaultSettingsModel } from '../xpanse-mapper-types/xpanse-default-setting/model/xpanse-default-setting.model';
// Services
import { ProductsService } from '../products/service/products.service';
import { LenderService } from '../service/lender.service';
import { AcuityService } from '../service/acuity.service';
import { CIProductsService } from '../service/ci-products.service';
import { NexsysOrderMessageService } from '../nexsys-mapper-types/service/nexsys-order-message.service';
import { AppScopeService } from '../service/app-scope.service';
import { NexsysAssetsOnCompletionService } from '../service/nexsys-assets-on-completion.service';
import { GenericIntegrationService } from '../service/generic-integration.service';
import { ValuTrustService } from '../valutrust-mapper-types/service/valutrust.service';
import { DefaultSettingsModel as FNCDefaultSettingsModel } from '../fnc-mapper-types/fnc-default-setting/model/fnc-default-setting.model';
import {DeliveryAssetsService} from "../delivery-assets/service/delivery-assets.service";
import { CmpAcuityService } from "../service/acuity-cmp.service";

@State<GlobalConfigurationStateModel>({
	name: 'globalConfiguration',
	defaults: {
		acuity: {},
		appScope: {},
		cmpAcuity:{},
		classValuation: {},
		classIntelligence: {},
		nexsys: {},
		valuTrust: {},
		xpanse: {},
		fnc: {},
		genericIntegrations: {},
		deliveryAssets: {},
		trackingSystems: [],
		enums: {},
		handleMode: []
	},
	children: [NexsysMonitorState, ProductState, GenericIntegrationState]
})
@Injectable()
export class GlobalConfigurationState {
	constructor(
		private productService: ProductsService,
		private lenderService: LenderService,
		private acuityService: AcuityService,
		private appScopeService: AppScopeService,
		private nexsysAssetsOnCompletionService: NexsysAssetsOnCompletionService,
		private nexsysMilestoneMsgService: NexsysOrderMessageService,
		private ciProductsService: CIProductsService,
		private valutrustMilestoneMsgService: ValuTrustService,
		private genericIntegrationService: GenericIntegrationService,
		private deliveryAssetsService: DeliveryAssetsService,
		private cmpAcuityService: CmpAcuityService
	) {
	}

	/**
	 * Get Feature Quality State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getGlobalConfiguration(
		state: GlobalConfigurationStateModel
	): GlobalConfigurationStateModel {
		return state;
	}

	/**
	 * Get product list appScope State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.appScope.products;
	}

	/**
	 * Get product list Acuity State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.acuity.products;
	}

	/**
	 * Get product list Acuity State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.cmpAcuity.products;
	}

	/**
	 * Get acuity lender branches.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityLenderBranches(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.acuity.lenderBranches;
	}

	/**
	 * Get acuity lender branches.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityLenderBranches(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.cmpAcuity.lenders;
	}

	/**
	 * Get lenders list.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getLenders(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.appScope.lenders;
	}

	/**
	 * Get product list Nexsys State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getNexsysProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.nexsys.products;
	}

	/**
	 * Get acuity milestone state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getValutrustAcuityMilestoneStatus(state: GlobalConfigurationStateModel): Array<string> {
		return state.valuTrust.statusMessageAcuity;
	}

	/**
	* Get acuity milestone state
	* @param state: GlobalConfigurationStateModel
	*/
	@Selector()
	static getValutrustAppScopeMilestoneStatus(state: GlobalConfigurationStateModel): Array<string> {
		return state.valuTrust.statusMessageAppScope;
	}

	/**
	 * Get product list ValuTrust State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getValuTrustProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.valuTrust.products;
	}

	/**
	 * Get product list Xpanse State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getXpanseProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.xpanse.products;
	}

	/**
	 * Get product list FNC State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getFNCProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.fnc.products;
	}

	/**
	 * Get product list inside Maps State Model.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getInsideMapsProducts(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.acuity.insideMapsProducts;
	}

	/**
	 * Get Nexsys attribute list.
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getNexsysAttributeList(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.nexsys.attributesList;
	}

	/**
	 * Get AppScope Document types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.appScope.documentTypes;
	}

	/**
	 * Get Nexsys Mapper Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopePropertyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.appScope.propertyTypes;
	}

	/**
	 * Get Nexsys Mapper Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeLoanTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.appScope.loanTypes;
	}

	/**
	 * Get Nexsys Mapper Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeLoanReasons(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.appScope.loanReasons;
	}

	/**
	 * Get Acuity Document types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.acuity.documentTypes;
	}

	/**
	 * Get Acuity Document types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.cmpAcuity.documentTypes;
	}

	/**
	 * Get Class Document types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getClassValuationDocTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.classValuation.docTypes;
	}

	/**
	 * Get Property Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityPropertyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.acuity.propertyTypes;
	}

	/**
	 * Get Property Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityPropertyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.cmpAcuity.propertyTypes;
	}

	/**
	 * Get AppScope Loan Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityLoanTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.acuity.loanTypes;
	}

	/**
	 * Get AppScope Loan Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityLoanTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.cmpAcuity.loanTypes;
	}

	/**
	 * Get Loan Reasons state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityLoanReasons(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.acuity.loanReasons;
	}

	/**
	 * Get Loan Reasons state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityLoanReasons(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.cmpAcuity.loanReasons;
	}

	/**
	 * Get Occupancy Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAcuityOccupancyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.acuity.occupancyTypes;
	}

	/**
	 * Get Occupancy Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getCmpAcuityOccupancyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.cmpAcuity.occupancyTypes;
	}

	/**
	 * Get acuity milestone state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getNexsysAcuityMilestoneStatus(state: GlobalConfigurationStateModel): Array<string> {
		return state.nexsys.statusMessageAcuity;
	}

	/**
	 * Get appscope milestone state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getNexsysAppScopeMilestoneStatus(state: GlobalConfigurationStateModel): Array<string> {
		return state.nexsys.statusMessageAppScope;
	}

	/**
	 * Get cmp acuity milestone state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getNexsysCmpAcuityMilestoneStatus(state: GlobalConfigurationStateModel): Array<string> {
		return state.nexsys.statusMessageCmpAcuity;
	}

	/**
	 * Get Occupancy Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeOccupancyTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.appScope.occupancyTypes;
	}

	/**
	 * Get Lenders Appscope state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getAppScopeLenders(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.appScope.lenders;
	}

	/**
	 * Get Form Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getFormTypes(state: GlobalConfigurationStateModel): Array<any> {
		return state.classIntelligence.formTypes;
	}

	/**
	 * Get Origins
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getOrigins(state: GlobalConfigurationStateModel): Array<any> {
		return state.classIntelligence.origins;
	}

	/**
	 * Get default settings for xpanse
	 * @param state: GlobalConfigurationStateModel
	 */
	 @Selector()
	 static getXpanseSettings(state: GlobalConfigurationStateModel): DefaultSettingsModel {
		 return state.xpanse.defaultSettings;
	 }

	/**
	 * Get default settings for FNC
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getFNCSettings(state: GlobalConfigurationStateModel): FNCDefaultSettingsModel {
		return state.fnc.defaultSettings;
	}

	/**
	 * Get generic integrations
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getGenericIntegrations(state: GlobalConfigurationStateModel): Array<GenericIntegration> {
		return state.genericIntegrations.integrations;
	}

	/**
	 * Get Occupancy Types state
	 * @param state: GlobalConfigurationStateModel
	 */
	@Selector()
	static getGenericIntegrationsIdNameObjectType(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.genericIntegrations.integrations.map(integration => {
			return {
				id: integration.integrationId,
				name: integration.integrationName,
			}
		});
	}

	/**
	 * Get generic integrations
	 * @param state
	 */
	@Selector()
	static getDeliveryAssetsAppScopeDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.deliveryAssets.appScope.documentTypes;
	}

	/**
	 * Get generic integrations
	 * @param state
	 */
	@Selector()
	static getDeliveryAssetsAcuityDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.deliveryAssets.acuity.documentTypes;
	}

	/**
	 * Get generic integrations
	 * @param state
	 */
	@Selector()
	static getDeliveryAssetsClassValuationDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.deliveryAssets.classValuation.documentTypes;
	}

	/**
	 * Get generic integrations
	 * @param state
	 */
	@Selector()
	static getDeliveryAssetsCmpAcuityDocumentTypes(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.deliveryAssets.cmpAcuity.documentTypes;
	}

	/**
	 * Get product list Acuity State Model.
	 * @param state
	 */
	@Selector()
	static getCmpAcuityMilestones(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.cmpAcuity.milestones;
	}

	/**
	 * Get product list Acuity State Model.
	 * @param state
	 */
	@Selector()
	static getAcuityMilestones(state: GlobalConfigurationStateModel): Array<ProductsList> {
		return state.acuity.milestones;
	}

	/**
	 * Get list of Tracking Systems
	 * @param state
	 */
	@Selector()
	static getTrackingSystems(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.trackingSystems;
	}

	/**
	 * Get list of handleMode
	 * @param state
	 */
	@Selector()
	static gethandleMode(state: GlobalConfigurationStateModel): Array<string> {
		return state.handleMode;
	}

	/**
	 * Get list of actions
	 * @param state
	 */
	@Selector()
	static getActionOptions(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.enums.actions;
	}

	/**
	 * Get CMP on hold reasons options
	 * @param state
	 */
	@Selector()
	static getCMPOnHoldReasonsOptions(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.enums.cmpOnHoldReasons;
	}

	/**
	 * Get list of receive from client options
	 * @param state
	 */
	@Selector()
	static getReceiveFromClientOptions(state: GlobalConfigurationStateModel): Array<IdNameObjectType> {
		return state.enums.receiveFromClient;
	}

	/**
	 * Save the current selected section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetProductListByIntegration)
	setProductListByIntegration(
		ctx: StateContext<GlobalConfigurationStateModel>,
		{ product }: SetProductListByIntegration
	): any {
		return this.productService.getProdcutListByIntegretion(product).pipe(
			tap(result => {
				const state = ctx.getState();
				switch (product) {
					case INTEGRATIONS.APPSCOPE:
						ctx.patchState({
							...state,
							appScope: { ...state.appScope, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.ACUITY:
						ctx.patchState({
							...state,
							acuity: { ...state.acuity, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.CMP_ACUTIY:
						ctx.patchState({
							...state,
							cmpAcuity: { ...state.cmpAcuity, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.INSIDEMAPS:
						ctx.patchState({
							...state,
							acuity: { ...state.acuity, ...{ insideMapsProducts: result } }
						});
						break;
					case INTEGRATIONS.NEXSYS:
						ctx.patchState({
							...state,
							nexsys: { ...state.nexsys, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.VALUTRUST:
						ctx.patchState({
							...state,
							valuTrust: { ...state.valuTrust, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.XPANSE:
						ctx.patchState({
							...state,
							xpanse: { ...state.xpanse, ...{ products: result } }
						});
						break;
					case INTEGRATIONS.FNC:
						ctx.patchState({
							...state,
							fnc: { ...state.fnc, ...{ products: result } }
						});
						break;
				}
			})
		);
	}

	/**
	 * Save the Nexsys attribute list
	 * @param ctx
	 * @param payload
	 */
	@Action(SetNexsysAttributeList)
	setNexsysAttributeList(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.productService.getNexsysListAttributes().pipe(
			tap(result => {
				const state = ctx.getState();
				const nexsys = { ...state.nexsys, ...{ attributesList: result } };
				ctx.patchState({
					...state,
					nexsys: nexsys
				});
			})
		);
	}

	/**
	 * Save the current selected section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetNexsysMileStoneMessageStatus)
	setNexsysMileStoneMessageStatus(
		ctx: StateContext<GlobalConfigurationStateModel>,
		{ origin }: SetNexsysMileStoneMessageStatus
	): any {
		return this.nexsysMilestoneMsgService.getNexsysOrderMessageStatus(origin).pipe(
			tap(result => {
				const state = ctx.getState();
				switch (origin) {
					case INTEGRATIONS.APPSCOPE:
						const appScopeMsg = { ...state.nexsys, statusMessageAppScope: result };
						ctx.patchState({
							...state,
							nexsys: appScopeMsg
						});
						break;
					case INTEGRATIONS.ACUITY:
						const acuityMsg = { ...state.nexsys, statusMessageAcuity: result };
						ctx.patchState({
							...state,
							nexsys: acuityMsg
						});
						break;
					case INTEGRATIONS.CMP_ACUTIY:
						const cmpAcuityMsg = { ...state.nexsys, statusMessageCmpAcuity: result };
						ctx.patchState({
							...state,
							nexsys: cmpAcuityMsg
						});
						break;
				}
			})
		);
	}

	/**
	 * Save the current selected section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetValutrustMileStoneMessageStatus)
	setValutrustMileStoneMessageStatus(
		ctx: StateContext<GlobalConfigurationStateModel>,
		{ origin }: SetValutrustMileStoneMessageStatus
	): any {
		return this.valutrustMilestoneMsgService.getValuTrustMilestoneMessagesStatus(origin).pipe(
			tap(result => {
				const state = ctx.getState();
				switch (origin) {
					case INTEGRATIONS.APPSCOPE:
						const appScopeMsg = { ...state.valuTrust, statusMessageAppScope: result };
						ctx.patchState({
							...state,
							valuTrust: appScopeMsg
						});
						break;
					case INTEGRATIONS.ACUITY:
						const acuityMsg = { ...state.valuTrust, statusMessageAcuity: result };
						ctx.patchState({
							...state,
							valuTrust: acuityMsg
						});
						break;
					case INTEGRATIONS.CMP_ACUTIY:
						const cmpAcuityMsg = { ...state.valuTrust, statusMessageAcuity: result };
						ctx.patchState({
							...state,
							valuTrust: cmpAcuityMsg
						});
						break;
				}
			})
		);
	}

	/**
	 * Save the acuity lender branches section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityLenderBranches)
	SetAcuityLenderBranches(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.lenderService.getAcuityLenderBranches().pipe(
			tap(result => {
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ lenderBranches: result } };

				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Save the lenders section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetLenders)
	SetLenders(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.lenderService.getLenders().pipe(
			tap(result => {
				const state = ctx.getState();
				const appScope = { ...state.appScope, ...{ lenders: result } };
				ctx.patchState({
					...state,
					appScope: appScope
				});
			})
		);
	}

	/**
	 * Set Property Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityPropertyTypes)
	SetAcuityPropertyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.acuityService.getPropertyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ propertyTypes: items } };
				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Set Loan Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityLoanTypes)
	SetAcuityLoanTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.acuityService.getLoanTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ loanTypes: items } };
				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Set Loan Reasons lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityLoanReasons)
	SetAcuityLoanReasons(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.acuityService.getLoanReasons().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ loanReasons: items } };
				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Set Occupancy Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityOccupancyTypes)
	SetAcuityOccupancyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.acuityService.getOccupancyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ occupancyTypes: items } };
				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Set Occupancy Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAppScopeOccupancyTypes)
	SetAppScopeOccupancyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.appScopeService.getOccupancyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const appScope = { ...state.appScope, ...{ occupancyTypes: items } };
				ctx.patchState({
					...state,
					appScope: appScope
				});
			})
		);
	}

	/**
	 * Set Nexsys Property Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAppScopePropertyTypes)
	SetAppScopePropertyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.appScopeService.getPropertyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const appScope = { ...state.appScope, ...{ propertyTypes: items } };
				ctx.patchState({
					...state,
					appScope: appScope
				});
			})
		);
	}

	/**
	 * Set Nexsys Loan Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAppScopeLoanTypes)
	SetAppScopeLoanTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.appScopeService.getLoanTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();

				const appScope = { ...state.appScope, ...{ loanTypes: items } };
				ctx.patchState({
					...state,
					appScope: appScope
				});
			})
		);
	}

	/**
	 * Set Nexsys Loan Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAppScopeLoanReasons)
	SetAppScopeLoanReasons(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.appScopeService.getLoanReasons().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();

				const appScope = { ...state.appScope, ...{ loanReasons: items } };
				ctx.patchState({
					...state,
					appScope: appScope
				});
			})
		);
	}

	/**
	 * Set Acuity Document Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityDocumentTypes)
	SetAcuityDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.nexsysAssetsOnCompletionService
			.getDocumentTypesByOrigin('Acuity').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const acuity = { ...state.acuity, ...{ documentTypes: items } };
					ctx.patchState({
						...state,
						acuity: acuity
					});
				})
			);
	}

	/**
	 * Set Appscope Document Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAppScopeDocumentTypes)
	SetAppScopeDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.nexsysAssetsOnCompletionService
			.getDocumentTypesByOrigin('AppScope').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					const state = ctx.getState();

					const appScope = { ...state.appScope, ...{ documentTypes: items } };
					ctx.patchState({
						...state,
						appScope: appScope
					});
				})
			);
	}

	/**
	 * Set ClassValuation Document Types lists.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetClassValuationDocumentTypes)
	SetClassValuationDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.genericIntegrationService.getClassDocuments()
			.pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					const state = ctx.getState();

					const classValuation = { ...state.classValuation, ...{ documentTypes: items } };
					ctx.patchState({
						...state,
						classValuation
					});
				})
			);
	}

	/**
	 * Set Enum Action list.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetActionOptions)
	SetActionOptions(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.genericIntegrationService.getActionOptions()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length 
								  ? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string })) 
								  : [];

					const state = ctx.getState();
					const enums = { ...state.enums, ...{ actions: items } };
	
					ctx.patchState({
						...state,
						enums
					});
				})
			);
	}

	/**
	 * Set Enum CMP On Hold Reasons.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetCMPOnHoldReasons)
	SetCMPOnHoldReasons(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.genericIntegrationService.getCMPOnHoldReasons()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length 
									? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string })) 
									: [];

					const state = ctx.getState();
					const enums = { ...state.enums, ...{ cmpOnHoldReasons: items } };
	
					ctx.patchState({
						...state,
						enums
					});
				})
			);
	}

	/**
	 * Set Enum CMP On Hold Reasons.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetReceiveFromClientOptions)
	SetReceiveFromClientOptions(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.genericIntegrationService.getReceiveFromClientOptions()
			.pipe(
				tap(result => {
					const items = result && Object.keys(result).length 
									? Object.entries(result).map(([, value]) => ({ id: value as string, name: value as string })) 
									: [];

					const state = ctx.getState();
					const enums = { ...state.enums, ...{ receiveFromClient: items } };
	
					ctx.patchState({
						...state,
						enums
					});
				})
			);
	}

	/**
	 * Set Form Types.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetFormTypes)
	SetFormTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.ciProductsService.getFormTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const classIntelligence = { ...state.classIntelligence, ...{ formTypes: items } };
				ctx.patchState({
					...state,
					classIntelligence: classIntelligence
				});
			})
		);
	}

	/**
	 * Set Origins.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetOrigins)
	SetOrigins(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.ciProductsService.getOrigins().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const classIntelligence = { ...state.classIntelligence, ...{ origins: items } };
				ctx.patchState({
					...state,
					classIntelligence: classIntelligence
				});
			})
		);
	}

	/**
	 * Set Xpanse default settings
	 * @param ctx
	 * @param defaultSettings
	*/
	@Action(SetXpanseSettings)
	setXpanseSettings(ctx: StateContext<GlobalConfigurationStateModel>,{ defaultSettings }: SetXpanseSettings): any {
		const state = ctx.getState();
		const xpanse = { ...state.xpanse, ...{ defaultSettings: defaultSettings } };
		return 	ctx.patchState({
			...state,
			xpanse: xpanse
		});
	}

	/**
	 * Set FNC default settings
	 * @param ctx
	 * @param defaultSettings
	*/
	@Action(SetFNCSettings)
	setFNCSettings(ctx: StateContext<GlobalConfigurationStateModel>,{ defaultSettings }: SetFNCSettings): any {
		const state = ctx.getState();
		const FNC = { ...state.fnc, ...{ defaultSettings: defaultSettings } };
		return ctx.patchState({
			...state,
			fnc: FNC
		});
	}

	/**
	 * Logout.
	 * @param ctx: GlobalConfigurationStateModel
	 */
	@Action(Logout)
	logout(ctx: StateContext<GlobalConfigurationStateModel>): void {
		ctx.setState({
			acuity: {},
			appScope: {},
			cmpAcuity: {},
			classValuation: {},
			classIntelligence: {},
			nexsys: {},
			valuTrust: {},
			xpanse: {},
			fnc: {},
			genericIntegrations: {},
			deliveryAssets: {},
			trackingSystems: [],
			enums: {},
			handleMode: [],
		});
	}

	/**
	 * Set Generic Integrations.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetGenericIntegrations)
	SetGenericIntegrations(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.genericIntegrationService.getIntegrations().pipe(
			tap(result => {
				const state = ctx.getState();
				const integrations = { ...state.genericIntegrations, ...{ integrations: result } };
				ctx.patchState({
					...state,
					genericIntegrations: integrations
				});
			})
		);
	}

	/**
	 * Set Generic Integrations.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetDeliveryAssetsAppScopeDocumentTypes)
	SetDeliveryAssetsAppScopeDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.deliveryAssetsService
			.getDocumentTypesByOrigin('AppScope').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const appScope = {...state.deliveryAssets, appScope: {documentTypes: items}};
					ctx.patchState({
						...state,
						deliveryAssets: appScope
					});
				})
			);
	}

	/**
	 * Set Generic Integrations.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetDeliveryAssetsAcuityDocumentTypes)
	SetDeliveryAssetsAcuityDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.deliveryAssetsService
			.getDocumentTypesByOrigin('Acuity').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const acuity = {...state.deliveryAssets, acuity: {documentTypes: items}};
					ctx.patchState({
						...state,
						deliveryAssets: acuity
					});
				})
			);
	}

	/**
	 * Set Generic Integrations.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetDeliveryAssetsClassValuationDocumentTypes)
	SetDeliveryAssetsClassValuationDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.deliveryAssetsService
			.getDocumentTypesByOrigin('class').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const classValuation = {...state.deliveryAssets, classValuation: {documentTypes: items}};
					ctx.patchState({
						...state,
						deliveryAssets: classValuation
					});
				})
			);
	}

	/**
	 * Event to set property types options for cmp acuity state
	 * @param ctx
	 * @constructor
	 */
	@Action(SetCmpAcuityPropertyTypes)
	SetCmpAcuityPropertyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.cmpAcuityService.getPropertyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ propertyTypes: items } };
				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Event to set loan types options for cmp acuity state
	 * @param ctx
	 * @constructor
	 */
	@Action(SetCmpAcuityLoanTypes)
	SetCmpAcuityLoanTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.cmpAcuityService.getLoanTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ loanTypes: items } };
				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Event to set loan reasons options for cmp acuity state
	 * @param ctx
	 * @constructor
	 */
	@Action(SetCmpAcuityLoanReasons)
	SetCmpAcuityLoanReasons(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.cmpAcuityService.getLoanReasons().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ loanReasons: items } };
				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Event to set occupancy types options for cmp acuity state
	 * @param ctx
	 * @constructor
	 */
	@Action(SetCmpAcuityOccupancyTypes)
	SetCmpAcuityOccupancyTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.cmpAcuityService.getOccupancyTypes().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ occupancyTypes: items } };
				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Event to set document types options for cmp acuity state
	 * @param ctx
	 * @constructor
	 */
	@Action(SetCmpAcuityDocumentTypes)
	SetCmpAcuityDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.nexsysAssetsOnCompletionService
			.getDocumentTypesByOrigin('cmpAcuity').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const cmpAcuity = { ...state.cmpAcuity, ...{ documentTypes: items } };
					ctx.patchState({
						...state,
						cmpAcuity: cmpAcuity
					});
				})
			);
	}

	/**
	 * Event to set document types options for class valuation state in products configuration
	 * @param ctx
	 * @constructor
	 */
	@Action(SetClassValuationDocTypes)
	SetClassValuationDocTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.nexsysAssetsOnCompletionService
			.getDocumentTypesByOrigin('class').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const classValuation = { ...state.classValuation, ...{ docTypes: items } };
					ctx.patchState({
						...state,
						classValuation: classValuation
					});
				})
			);
	}

	/**
	 * Set Generic Integrations.
	 * @param ctx
	 * @param payload
	 */
	@Action(SetDeliveryAssetsCmpAcuityDocumentTypes)
	SetDeliveryAssetsCmpAcuityDocumentTypes(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.deliveryAssetsService
			.getDocumentTypesByOrigin('cmpacuity').pipe(
				tap(result => {
					let items = [];
					if (result && Object.keys(result).length) {
						items = Object.keys(result).map(key => ({ id: key, name: result[key] }));
					}
					//items.push({ id: 'AppraisalReport', name: 'AppraisalReport-PFR' });
					items = items.sort((a, b) => (a.name > b.name) ? 1 : -1);
					const state = ctx.getState();
					const cmpAcuity = {...state.deliveryAssets, cmpAcuity: {documentTypes: items}};
					ctx.patchState({
						...state,
						deliveryAssets: cmpAcuity
					});
				})
			);
	}

	/**
	 * Save the cmp acuity lender branches section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetCmpAcuityLenderBranches)
	SetCmpAcuityLenderBranches(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.lenderService.getCmpAcuityLenderBranches().pipe(
			tap(result => {
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ lenders: result } };

				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Save the cmp acuity lender branches section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetAcuityMilestones)
	SetAcuityMilestones(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.productService.getMilestoneList('acuity').pipe(
			tap(result => {
				const state = ctx.getState();
				const acuity = { ...state.acuity, ...{ milestones: result } };

				ctx.patchState({
					...state,
					acuity: acuity
				});
			})
		);
	}

	/**
	 * Save the cmp acuity lender branches section
	 * @param ctx
	 * @param payload
	 */
	@Action(SetCmpAcuityMilestones)
	SetCmpAcuityMilestones(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.productService.getMilestoneList('cmpacuity').pipe(
			tap(result => {
				const state = ctx.getState();
				const cmpAcuity = { ...state.cmpAcuity, ...{ milestones: result } };

				ctx.patchState({
					...state,
					cmpAcuity: cmpAcuity
				});
			})
		);
	}

	/**
	 * Event to save in store tracking systems
	 * @param ctx
	 * @constructor
	 */
	@Action(SetTrackingSystems)
	SetTrackingSystems(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.productService.getTrackingSystems().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => ({ id: item.id, name: item.title }));
				}

				const state = ctx.getState();

				ctx.patchState({
					...state,
					trackingSystems : items
				});
			})
		);
	}

	/**
	 * Event to save in store Handle Mode
	 * @param ctx
	 * @constructor
	 */
	@Action(SetHandleMode)
	SetHandleMode(ctx: StateContext<GlobalConfigurationStateModel>): any {
		return this.productService.getHandleMode().pipe(
			tap(result => {
				let items = [];
				if (result && result.length) {
					items = result.map(item => item);
				}

				const state = ctx.getState();

				ctx.patchState({
					...state,
					handleMode : items
				});
			})
		);
	}
}
